import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, lastValueFrom, of, catchError, switchMap, throwError } from 'rxjs';
import { Settings, SettingsMimesys, SettingsFitMade4U, Settings360Depict } from '../models/settings.model';
import { environment } from 'src/environments/environment';
import * as utils from '../../shared/helpers/utils';
import { License } from '../models/license.model';
import { Package } from '../models/package.model';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  setDefaultLang = (): string => {
    //this.translateService.addLangs(['es-ES', 'en-GB', 'he']);
    let browserLang = utils.getBrowserLan();
    if (this.translateService.getLangs().includes(browserLang)) {
      return browserLang
    } else {
      return 'en-GB'
    }
  }

  defaultSettings = {
    language: this.setDefaultLang(),
    login: {
      wallpaper: {
        background: 'color',
        backgroundColor: 'black',
        backgroundImage: ''
      },
      header: {
        backgroundColor: 'transparent'
      },
      form: {
        position: 'center',
        backgroundColor: 'white'
      },
    },
    layout: {
      sidebar: {
        color: 'black',
        background: 'color',
        backgroundColor: 'white',
        backgroundImage: ''
      },
    },
    logo: {
      1: {
        image: '',
        configurable: false
      },
      2: {
        image: '',
        configurable: false
      },
      3: {
        image: '',
        configurable: false
      },
      4: {
        image: '',
        configurable: false
      },
      5: {
        image: '',
        configurable: false
      },
      6: {
        image: '',
        configurable: false
      },
    }
  } as Settings;

  settingsStore = new BehaviorSubject(this.defaultSettings);

  constructor(
    private httpClient: HttpClient,
    private translateService: TranslateService
  ) { }

  getByLoginToken(loginToken: string): Observable<Settings> {
    if (loginToken) {
      return this.httpClient.get<Settings>(environment.apiUrl + '/settings/logintoken/' + loginToken);
    } else {
      return of(this.defaultSettings)
    }
  }

  get(entityId: number, appId: number, hierarchy?: string): Observable<any> {
    return this.httpClient.get<Settings>(`${environment.apiUrl}/entities/${entityId}/apps/${appId}/settings${hierarchy ? '?hierarchy=' + hierarchy : ''}`)
    //return this.httpClient.get<Settings>(`${environment.apiUrl}/entities/${entityId}/apps/${appId}/settings`)
  }

  update(entityId: number, appId: number, data: any): Observable<any> {
    const emptyStringParamsNames = ['distributorDomain', 'fromMail'];
    let emptyStringParams: { [key: string | number]: any } = {};
    emptyStringParamsNames.map((param: string) => data[param] === '' ? emptyStringParams[param] = '' : null)
    // cleanParams strips nulls and then discards the objects it leaves empty, so a cleared
    // dashboard image never reaches the API. The block already carries null for "clear" and
    // is omitted altogether when the entity cannot configure it, so send it untouched.
    const dashboardParams = data.dashboard ? { dashboard: data.dashboard } : {};
    return this.httpClient.patch<Settings>(environment.apiUrl + '/entities/' + entityId + '/apps/' + appId + '/settings', { ...utils.cleanParams(data), ...emptyStringParams, ...dashboardParams });

  }

  async getBase64Image(url: string): Promise<string> {
    const file = await lastValueFrom(this.httpClient.get(url, { responseType: 'blob' }));
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file as Blob);
      reader.onload = () => resolve(reader.result?.toString() ?? '');
      reader.onerror = reject;
    });
  }

  getPackage(entityId: number): Observable<Package> {
    return this.httpClient.get<Package>(`${environment.apiUrl}/packages/${entityId}`)
  }

  getMimesys(entityId: number | string, hierarchy: string |null, entityTypeId: number | null, email: string | null, lang: string, isFromResult = false): Observable<SettingsMimesys> {
    return this.httpClient.get<SettingsMimesys>(`${environment.apiSettingsFitUrl}/settings/mimesys?shop_id=${entityId}&hierarchy=${hierarchy}&entity_type_id=${entityTypeId}&lang=${lang}&email=${email}&fromResults=${isFromResult}`)
  }

  getMimesysVideo(entityId: number | string, hierarchy: string |null): Observable<any> {
    return this.httpClient.get<SettingsMimesys>(`${environment.apiSettingsFitUrl}/settings/mimesys/screensaver?shop_id=${entityId}&hierarchy=${hierarchy}`)
  }

  updateMimesys(entityId: number | string, hierarchy: string |null, entityTypeId: number, data: any): Observable<any> {
    return this.httpClient.post<SettingsMimesys>(`${environment.apiSettingsFitUrl}/settings/mimesys${entityId ? '?shop_id=' + entityId : ''}${hierarchy ? '&hierarchy=' + hierarchy : ''}&entity_type_id=${entityTypeId}`, data);
  }

  uploadVideoMimesys(entityId: number | string, data: any): Observable<any> {
    //const headers = { 'Accept': 'application/json' };
    return this.httpClient.post<SettingsMimesys>(`${environment.apiSettingsFitUrl}/settings/mimesys/screensaver?shop_id=${entityId}`, data);
    //return this.httpClient.put<Settings>(`${environment.apiSettingsFitUrl}/api/settings/upload/${entityId}?fileName=${_name}&chunk_index=${_chunk_index}&totalChunks=${_totalChunks}`, data, { headers: headers });
  }

  upload2VideoMimesys(entityId: number, video: any, videoName: string): Observable<any> {
    //const headers = { "Content-Type": "multipart/form-data" };
    const formData: FormData = new FormData();
    formData.append('video', video, videoName);
    return this.httpClient.put<Settings>(`${environment.apiSettingsFitUrl}/api/settings/upload2/${entityId}`, formData);
    //return this.httpClient.post<Settings>(`${environment.apiMimesysUrl}/api/settings/upload2/${entityId}`, formData);
  }

  getInvision(entityId: number | string, hierarchy: string |null, entityTypeId: number): Observable<SettingsFitMade4U> {
    return this.httpClient.get<SettingsFitMade4U>(`${environment.apiSettingsFitUrl}/settings/invision?shop_id=${entityId}&hierarchy=${hierarchy}&entity_type_id=${entityTypeId}`)
  }

  updateInvisionScreenSaver(entityId: number | string, data: any): Observable<any> {
    return this.httpClient.post<SettingsFitMade4U>(`${environment.apiSettingsFitUrl}/settings/invision/screensaver?shop_id=${entityId}`, data);
  }

  updateInvision(entityId: number | string, hierarchy: string |null, entityTypeId: number, data: any): Observable<any> {
    return this.httpClient.post<SettingsFitMade4U>(`${environment.apiSettingsFitUrl}/settings/invision${entityId ? '?shop_id=' + entityId : ''}${hierarchy ? '&hierarchy=' + hierarchy : ''}&entity_type_id=${entityTypeId}`, data);
  }

  getThickness(entityId: number | string, hierarchy: string |null, entityTypeId: number): Observable<Settings360Depict> {
    return this.httpClient.get<Settings360Depict>(`${environment.apiSettingsFitUrl}/settings/depict?shop_id=${entityId}&hierarchy=${hierarchy}&entity_type_id=${entityTypeId}`)
  }

  updateThickness(entityId: number | string, hierarchy: string |null, entityTypeId: number, data: any): Observable<any> {
    return this.httpClient.post<Settings360Depict>(`${environment.apiSettingsFitUrl}/settings/depict${entityId ? '?shop_id=' + entityId : ''}${hierarchy ? '&hierarchy=' + hierarchy : ''}&entity_type_id=${entityTypeId}`, data);
  }


  getOrCreateThickness(entityId: number | string, hierarchy: string | null, entityTypeId: number, timezone: string): Observable<Settings360Depict> {
    return this.getThickness(entityId, hierarchy, entityTypeId).pipe(
      catchError(err => err.status !== 404
        ? throwError(() => err)
        : this.updateThickness(entityId, hierarchy, entityTypeId, { settings: { timezone } }).pipe(
            switchMap(() => this.getThickness(entityId, hierarchy, entityTypeId))
          ))
    );
  }

  putScreenSaver(url: string,  data: any): Observable<any> {
    return this.httpClient.put<SettingsFitMade4U>(url, data);
  }

}
